import ApplicationRow from '@/components/shared/ApplicationRow';
import ApplicationTableHeader from '@/components/shared/ApplicationTableHeader';
import CertificateViewModal from '@/components/sharedModals/CertificateViewModal';
import ReleaseConfirmationModal from '@/components/sharedModals/ReleaseConfirmationModal';
import ApplicationViewModal from '@/components/sharedModals/applicationViewModal/ApplicationViewModal';
import useActionHotkeys from '@/hooks/useActionHotkeys';
import useEscapeKey from '@/hooks/useEscapeKey';
import { gql, useMutation, useQuery } from '@apollo/client';
import { ClipboardCheck, Eye, FileCheck2, FileText, RotateCcw } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import RRC_RecordModal from './RRC_RecordModal';

const UPDATE_PERMIT_STAGE = gql`
   mutation UpdatePermitStage(
      $id: ID!,
      $currentStage: String!,
      $status: String!,
      $notes: String,
      $recordedByReceivingClerk: Boolean,
      $acceptedByPENRCENROfficer: Boolean
   ) {
      updatePermitStage(
         id: $id,
         currentStage: $currentStage,
         status: $status,
         notes: $notes,
         recordedByReceivingClerk: $recordedByReceivingClerk,
         acceptedByPENRCENROfficer: $acceptedByPENRCENROfficer
      ) {
         id
         currentStage
         status
         recordedByReceivingClerk
         acceptedByPENRCENROfficer
         history {
            notes
            timestamp
         }
      }
   }
`;

const UNDO_RECORD_APPLICATION = gql`
   mutation UndoRecordApplication($id: ID!, $approvedByPENRCENROfficer: Boolean) {
      undoRecordApplication(id: $id, approvedByPENRCENROfficer: $approvedByPENRCENROfficer) {
         id
         acceptedByPENRCENROfficer
         reviewedByChief
         history {
            notes
            timestamp
         }
      }
   }
`;

const UPDATE_CERTIFICATE = gql`
   mutation UpdateCertificate($id: ID!, $certificateStatus: String) {
      updateCertificate(id: $id, certificateStatus: $certificateStatus) {
         id
         certificateStatus
      }
   }
`;

const GET_CERTIFICATE = gql`
   query GetCertificatesByApplicationId($applicationId: ID!) {
      getCertificatesByApplicationId(applicationId: $applicationId) {
         id
         certificateNumber
         applicationId
         applicationType
         certificateStatus
         dateCreated
         dateIssued
         expiryDate
         forestRangerSignature {
            data
            contentType
         }
         chiefTSDSignature {
            data
            contentType
         }
         penrCenrOfficerSignature {
            data
            contentType
         }
         csawCertificateData {
            registrationType
            ownerName
            address
            chainsawDetails {
               brand
               model
               serialNumber
               dateOfAcquisition
               powerOutput
               maxLengthGuidebar
               countryOfOrigin
               purchasePrice
            }
            purpose
            otherDetails
         }
         covCertificateData {
            applicationNumber
            name
            driverName
            driverAddress
            originAddress
            destinationAddress
            forestProducts {
               kind
               species
               scientificName
               numberOfTrees
               quantity {
                  value
                  unit
               }
               volume {
                  cubicMeters
                  boardFeet
               }
            }
            conveyance
            vehiclePlateNumber
            consignee
            permitNumber
         }
         orderOfPayment {
            id
            OOPstatus
            officialReceipt {
               orNumber
               dateIssued
               amount
               paymentMethod
               remarks
            }
         }
      }
   }
`;

const RRC_ApplicationRow = ({ app, onRecordComplete, currentTab, isMobile, isFirstRow }) => {
   const [isViewModalOpen, setIsViewModalOpen] = useState(false);
   const [isRecordModalOpen, setIsRecordModalOpen] = useState(false);
   const [isCertificateViewModalOpen, setIsCertificateViewModalOpen] = useState(false);
   const [isReleaseModalOpen, setIsReleaseModalOpen] = useState(false);
   const [updatePermitStage] = useMutation(UPDATE_PERMIT_STAGE);
   const [undoRecordApplication] = useMutation(UNDO_RECORD_APPLICATION);
   const [updateCertificate] = useMutation(UPDATE_CERTIFICATE);

   const { loading: certLoading, error: certError, data: certData } = useQuery(GET_CERTIFICATE, {
      variables: { applicationId: app.id },
      fetchPolicy: 'network-only',
      pollInterval: 10000
   });

   const handleRecordComplete = () => {
      setIsRecordModalOpen(false);
      onRecordComplete();
   };

   const handleUndo = async () => {
      try {
         const { data } = await undoRecordApplication({
            variables: {
               id: app.id,
               currentStage: 'ForRecordByReceivingClerk',
               status: 'In Progress',
               acceptedByPENRCENROfficer: false,
               reviewedByChief: false
            }
         });

         if (data?.undoRecordApplication) {
            toast.success('Application status undone successfully');
            onRecordComplete();
         } else {
            toast.error('Failed to undo record status, already accepted by PENR/CENR Officer or reviewed by Chief RPS/TSD');
         }
      } catch (error) {
         console.error('Error undoing record:', error);
         if (error.message.includes('already accepted by PENR/CENR Officer')) {
            toast.error('Cannot undo: Application already accepted by PENR/CENR Officer');
         } else if (error.message.includes('already reviewed by Chief RPS/TSD')) {
            toast.error('Cannot undo: Application already reviewed by Chief RPS/TSD');
         } else {
            toast.error(error.message || 'Failed to undo record status');
         }
      }
   };

   const handleUndoRelease = async () => {
      try {
         const result = await updatePermitStage({
            variables: {
               id: app.id,
               currentStage: 'PendingRelease',
               recordedByReceivingClerk: false,
               status: 'In Progress',
               notes: 'Release undone by Receiving/Releasing Clerk'
            }
         });

         // Update certificate status
         if (certData?.getCertificatesByApplicationId[0]?.id) {
            await updateCertificate({
               variables: {
                  id: certData.getCertificatesByApplicationId[0].id,
                  certificateStatus: 'Pending Release'
               }
            });
         }

         if (result?.data?.updatePermitStage) {
            toast.success('Certificate release undone successfully');
            onRecordComplete();
         }
      } catch (error) {
         console.error('Error undoing release:', error);
         toast.error(`Failed to undo release: ${error.message}`);
      }
   };

   // Define actions for the application row
   const actions = [
      {
         icon: Eye,
         label: "View Application",
         onClick: () => setIsViewModalOpen(true),
         variant: "outline",
         className: "view-action",
         hotkey: "Ctrl + 1"
      },
      app.currentStage === 'ForRecordByReceivingClerk' && !app.recordedByReceivingClerk && {
         icon: ClipboardCheck,
         label: "Record Application",
         onClick: () => setIsRecordModalOpen(true),
         variant: "outline",
         className: "text-blue-600 hover:text-blue-800",
         hotkey: "Ctrl + 2"
      },
      currentTab === 'Reviewed/Recorded Applications' && {
         icon: RotateCcw,
         label: "Undo Record",
         onClick: handleUndo,
         variant: "outline",
         className: "text-yellow-600 hover:text-yellow-800"
      },
      currentTab === 'Pending Release' && {
         icon: FileCheck2,
         label: 'Release Certificate',
         onClick: () => setIsReleaseModalOpen(true),
         variant: "outline",
         className: "release-action text-blue-600 hover:text-blue-800",
         hotkey: "Ctrl + 3"
      },
      currentTab === 'Released Certificates' && {
         icon: RotateCcw,
         label: "Undo Release",
         onClick: handleUndoRelease,
         variant: "outline",
         className: "undo-action text-yellow-600 hover:text-yellow-800"
      },
      (currentTab === 'Pending Release' ||
         currentTab === 'Released Certificates' ||
         (app.hasCertificate && certData?.getCertificatesByApplicationId?.length > 0)) && {
         icon: FileText,
         label: "View Certificate",
         onClick: () => setIsCertificateViewModalOpen(true),
         variant: "outline",
         className: "view-certificate-action text-purple-600 hover:text-purple-800"
      }
   ].filter(Boolean);

   useEffect(() => {
      if (certError) {
         console.error('Error fetching certificate:', certError);
         toast.error('Error loading certificate');
      }
   }, [certError]);

   useActionHotkeys({
      actions,
      activeMainTab: currentTab,
      disabled: isViewModalOpen || isRecordModalOpen || isReleaseModalOpen ||
         isCertificateViewModalOpen
   });

   useEscapeKey(() => setIsViewModalOpen(false), isViewModalOpen);
   useEscapeKey(() => setIsRecordModalOpen(false), isRecordModalOpen);
   useEscapeKey(() => setIsReleaseModalOpen(false), isReleaseModalOpen);
   useEscapeKey(() => setIsCertificateViewModalOpen(false), isCertificateViewModalOpen);

   return (
      <>
         {isFirstRow && !isMobile && <ApplicationTableHeader />}
         <ApplicationRow
            application={app}
            actions={actions}
            isMobile={isMobile}
         />

         {/* Modals */}
         <ApplicationViewModal
            isOpen={isViewModalOpen}
            onClose={() => setIsViewModalOpen(false)}
            application={app}
         />
         <RRC_RecordModal
            isOpen={isRecordModalOpen}
            onClose={() => setIsRecordModalOpen(false)}
            application={app}
            onRecordComplete={handleRecordComplete}
         />
         <ReleaseConfirmationModal
            isOpen={isReleaseModalOpen}
            onClose={() => setIsReleaseModalOpen(false)}
            application={app}
            onComplete={onRecordComplete}
         />
         <CertificateViewModal
            isOpen={isCertificateViewModalOpen}
            onClose={() => setIsCertificateViewModalOpen(false)}
            certificate={certData?.getCertificatesByApplicationId[0]}
            loading={certLoading}
            error={certError}
         />
      </>
   );
};

export default RRC_ApplicationRow;
